/**
 * The Overview's Leverage & Capitalization card.
 *
 * It replaces a "Capital Stack" column chart that drew only the senior
 * tranche the client is underwriting — one solid block labelled 100% — and
 * did not fit its slot, so the reader scrolled to see a single number. What a
 * lender reads first about a loan is where it sits and what covers it: the
 * stack with the sponsor's equity under it, the attachment point (Senior
 * 0–75.0% LTC), and the four or five ratios the loan is sized on. That is
 * what this card holds, sized to its slot at 1366×768 and 1600×950.
 *
 * Figures without their inputs are not drawn as dashes. They are left out,
 * and one line names what they are waiting on — a deal with only a loan
 * amount shows the loan, its per-unit basis, and "Add a value (as-is) to see
 * leverage and equity", never an empty graphic.
 *
 * Unlocked, every value the card prints is edited where it is printed, and
 * only once on the screen. The card used to add an Inputs block repeating the
 * loan amount, value, NOI, debt service and units that the Loan Overview,
 * Property Snapshot and Cash Flow Summary beside it already edit in place —
 * two or three boxes for one field — while the stated Sponsor Equity it
 * prints had no editor anywhere on the Overview. Now a stated amount the
 * Overview does not otherwise edit (the equity, the purchase price or project
 * cost a stack closes on) is its input in place; a derived figure or implied
 * amount opens its inputs (R1); one the tables beside it edit says where; a
 * stored tranche says why it is read-only.
 *
 * One input the card still offers on its own: the basis the deal's purpose
 * sizes it on (an acquisition's purchase price, a construction loan's total
 * project cost) while the deal leaves it empty. No Loan Overview or Property
 * Snapshot row and no header fact names either field, so dropping the old
 * Inputs block left an acquisition with no price nothing to type it into on
 * the Overview, beside a line asking for one.
 */
import type { LoanFieldLineageField } from '@/pages/pipeline-intelligence/types'
import type { RawDealFile } from '@/services/dealFile'
import { asNumber, catalogueValue, formatAmount } from './format'
import {
  CapitalizationBar,
  EditableAmount,
  FigureMarks,
  InputsBlock,
  RequestTag,
  Swatch,
  UnsharedTranches,
  type EditedElsewhere,
} from './CapitalizationView'
import { useDossierEdit } from './editContext'
import { EditableValue } from './EditableValue'
import { CoverageExtras } from './CoverageExtras'
import { useCapitalization } from './useCapitalization'
import { attachmentRange, cardFigures, inputsFor, purposeBasisFields, type LeverageFigure } from './leverageModel'

/** Where the card's own basis input sits, for the one-editor note. */
const BASIS_INPUTS_PLACE = 'the Inputs below'

function blank(value: unknown): boolean {
  return value === null || value === undefined || value === ''
}

/**
 * The fields the Overview's own tables beside this card edit in place: the
 * Loan Overview and Property Snapshot rows (buildWorkspaceModel's
 * `loanOverview` / `propertyFields`, drawn by OverviewColumns' MetricTable).
 * The card draws no second input for them.
 */
const OVERVIEW_TABLE_FIELDS: Record<string, string> = {
  ltv: 'Loan Overview',
  ltc: 'Loan Overview',
  dscr: 'Loan Overview',
  debt_yield: 'Loan Overview',
  property_value: 'Property Snapshot',
  noi: 'Property Snapshot',
  cap_rate: 'Property Snapshot',
  units: 'Property Snapshot',
  sf: 'Property Snapshot',
}

/**
 * The field the Loan Overview's Requested Amount row edits: the commitment
 * where the deal records one apart from the loan amount, else the loan amount
 * (the same rule as buildWorkspaceModel's `requestedField`).
 */
function requestedField(get: (name: string) => unknown): 'loan_amount' | 'fully_funded_amount' {
  const loan = asNumber(get('loan_amount'))
  const initial = asNumber(get('initial_funding'))
  const future = asNumber(get('future_funding'))
  const commitment = asNumber(get('fully_funded_amount')) ?? (initial != null && future != null ? initial + future : undefined)
  const loanIsCommitment = commitment != null && loan != null && Math.abs(loan - commitment) < 1
  return commitment != null && !loanIsCommitment ? 'fully_funded_amount' : 'loan_amount'
}

/** A leverage figure, unlocked: a derived one opens its inputs; a stated one
 *  the tables beside the card edit says where. */
function FigureValue({ figure, editedElsewhere }: { figure: LeverageFigure; editedElsewhere: EditedElsewhere }) {
  if (figure.basis === 'derived' && !figure.field) {
    return <EditableValue name={figure.id} label={figure.label} display={figure.display} computed={figure.formula ?? ''} inputs={figure.inputs} />
  }
  if (figure.field) {
    const where = editedElsewhere(figure.field)
    return (
      <EditableValue
        name={figure.field}
        label={figure.label}
        display={figure.display}
        fields={where ? [] : undefined}
        readOnlyReason={where ? `Edited in ${where} on this screen — one editor per field.` : undefined}
      />
    )
  }
  return <>{figure.display}</>
}

export function LeverageCard({
  raw,
  loanId,
  lineage,
}: {
  raw: RawDealFile
  loanId: string
  lineage?: LoanFieldLineageField[]
}) {
  const { leverage, sourceOf, get } = useCapitalization({ raw, loanId, lineage })
  const edit = useDossierEdit()
  const cap = leverage.capitalization
  const figures = cardFigures(leverage)
  const request = requestedField(get)
  // The basis the purpose calls for, while the SAVED deal leaves it empty —
  // and still while a person types into it, so the box does not vanish (and
  // the stack's total row take over) at the first keystroke. A value the
  // engine's preview derived is an output, shown where the stack prints it.
  const basisInputs = inputsFor(
    purposeBasisFields(get('purpose')).filter(
      (name) => blank(catalogueValue(edit.raw ?? raw, edit.defs, name)) && (name in edit.draft || blank(get(name))),
    ),
  )
  const editedElsewhere: EditedElsewhere = (field) =>
    basisInputs.some((input) => input.name === field)
      ? BASIS_INPUTS_PLACE
      : field === request ? 'Loan Overview' : OVERVIEW_TABLE_FIELDS[field] ?? null

  return (
    <div data-testid="leverage-card" className="flex flex-col gap-1.5 text-[10px]">
      {cap ? (
        <>
          <CapitalizationBar capitalization={cap} />
          <ul className="space-y-0.5">
            {cap.segments.map((segment) => (
              <li key={segment.id} className="flex items-center gap-1.5">
                <Swatch color={segment.color} implied={segment.isImplied} />
                <span className="min-w-0 flex-1 truncate text-stone-300" title={segment.label}>{segment.label}</span>
                {segment.isRequested ? <RequestTag /> : null}
                <span className="w-[62px] shrink-0 text-right font-mono text-[9px] text-stone-500">{attachmentRange(segment)}</span>
                <span className="inline-flex shrink-0 items-center justify-end gap-1 font-mono text-stone-100">
                  <EditableAmount
                    id={segment.id}
                    label={segment.label}
                    display={formatAmount(segment.amount)}
                    amountField={segment.amountField}
                    amountFormula={segment.amountFormula}
                    amountInputs={segment.amountInputs}
                    isImplied={segment.isImplied}
                    readOnlyReason={segment.readOnlyReason}
                    editedElsewhere={editedElsewhere}
                    sourceOf={sourceOf}
                  />
                </span>
              </li>
            ))}
          </ul>
          <div className="flex items-center justify-between gap-2 border-t border-amber-300/15 pt-1 font-medium text-stone-200">
            <span className="truncate">{cap.totalLabel}{cap.totalImplied ? ' (implied)' : ''}</span>
            <span className="inline-flex items-center gap-1 font-mono">
              <EditableAmount
                id="total_capitalization"
                label={cap.totalLabel}
                display={formatAmount(cap.total)}
                amountField={cap.totalField}
                // A total no field holds is the tranches above, summed.
                amountFormula={cap.totalField ? undefined : 'the tranches above, summed'}
                editedElsewhere={editedElsewhere}
                sourceOf={sourceOf}
                marks={Boolean(cap.totalField)}
              />
            </span>
          </div>
        </>
      ) : leverage.tranches.length > 0 ? (
        <UnsharedTranches tranches={leverage.tranches} sourceOf={sourceOf} editedElsewhere={editedElsewhere} />
      ) : null}

      {figures.length > 0 ? (
        <dl data-testid="leverage-figures" className="grid grid-cols-2 gap-x-2 gap-y-1">
          {figures.map((figure) => (
            <div key={figure.id} data-figure={figure.id} className="min-w-0">
              <dt className="flex min-w-0 items-center gap-1 font-mono text-[8px] uppercase tracking-wide text-stone-500">
                <span className="min-w-0 truncate" title={figure.label}>{figure.label}</span>
                <FigureMarks figure={figure} sourceOf={sourceOf} />
              </dt>
              <dd className="break-words font-mono text-[12px] font-semibold leading-tight text-stone-100">
                <FigureValue figure={figure} editedElsewhere={editedElsewhere} />
              </dd>
              <dd className="truncate text-[8.5px] leading-tight text-stone-500" title={figure.operands ?? figure.formula}>
                {figure.basis === 'derived' ? figure.formula ?? 'computed' : 'as stated'}
              </dd>
            </div>
          ))}
        </dl>
      ) : null}

      <CoverageExtras model={leverage} />

      {leverage.waiting ? (
        <p data-testid="leverage-waiting" className="text-[9px] leading-snug text-stone-500">{leverage.waiting}</p>
      ) : null}

      <InputsBlock inputs={basisInputs} note="Not stated on this deal. Leverage and equity are sized on it." />
    </div>
  )
}
